import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { useRidesPermissions, RIDES_PAGE_ACCESS as access } from '@/hooks/rides/useRidesPermissions';
import { RidesGate } from './components';

// Eager load dashboard for instant access
import RidesDashboard from './dashboard';

// Lazy load all other pages
const DriversPage = lazy(() => import('./drivers'));
const DriverDetailPage = lazy(() => import('./drivers/detail'));
const VerificationsPage = lazy(() => import('./verifications'));
const DocumentsPage = lazy(() => import('./documents'));
const BookingsPage = lazy(() => import('./bookings'));
const ListingsPage = lazy(() => import('./listings'));
const DisputesPage = lazy(() => import('./disputes'));
const SecurityPage = lazy(() => import('./security'));
const CampusesPage = lazy(() => import('./campuses'));
const EventsPage = lazy(() => import('./events'));
const AdminSettingsPage = lazy(() => import('./admin/settings'));
const AdminLookupsPage = lazy(() => import('./admin/lookups'));
const AdminCopyPage = lazy(() => import('./admin/copy'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Carpool Management Routes
 *
 * Module access (Admin / RidesAdmin / RidesSecurity) is the domain gate; inside, pages gate
 * on capabilities (role OR Rides.* right) via RidesGate. Security-only users land on the
 * security desk — the rest of the console is admin-only. The per-page predicates live in
 * RIDES_PAGE_ACCESS, which the sidebar menu (ridesMenu.tsx) reads too, so a page shows in the
 * menu exactly when its route lets the person in.
 */
export default function RidesManagementRoutes() {
  const perms = useRidesPermissions();

  return (
    <MainLayout domainKey="rides">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - default route; security-only users go straight to the desk */}
          <Route path="/" element={perms.isSecurityOnly ? <Navigate to="/app/rides/security" replace /> : <RidesGate allow={access.dashboard}><RidesDashboard /></RidesGate>} />

          {/* Operations */}
          <Route path="/drivers" element={<RidesGate allow={access.drivers}><DriversPage /></RidesGate>} />
          <Route path="/drivers/:userId/*" element={<RidesGate allow={access.driverDetail}><DriverDetailPage /></RidesGate>} />
          <Route path="/verifications" element={<RidesGate allow={access.verifications}><VerificationsPage /></RidesGate>} />
          <Route path="/documents" element={<RidesGate allow={access.documents}><DocumentsPage /></RidesGate>} />
          <Route path="/bookings" element={<RidesGate allow={access.bookings}><BookingsPage /></RidesGate>} />
          <Route path="/listings" element={<RidesGate allow={access.listings}><ListingsPage /></RidesGate>} />
          <Route path="/disputes" element={<RidesGate allow={access.disputes}><DisputesPage /></RidesGate>} />

          {/* Security desk */}
          <Route path="/security" element={<RidesGate allow={access.security}><SecurityPage /></RidesGate>} />

          {/* Configuration */}
          <Route path="/campuses" element={<RidesGate allow={access.campuses}><CampusesPage /></RidesGate>} />
          <Route path="/events" element={<RidesGate allow={access.events}><EventsPage /></RidesGate>} />

          {/* Admin */}
          <Route path="/admin/settings" element={<RidesGate allow={access.settings}><AdminSettingsPage /></RidesGate>} />
          <Route path="/admin/copy" element={<RidesGate allow={access.copy}><AdminCopyPage /></RidesGate>} />
          <Route path="/admin/lookups" element={<Navigate to="/app/rides/admin/lookups/verification-statuses" replace />} />
          <Route path="/admin/lookups/:lookup" element={<RidesGate allow={access.lookups}><AdminLookupsPage /></RidesGate>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/rides" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
